import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Every namespace lives as its own locales/no/<Name>.json +
// locales/en/<Name>.json pair (see locales/namespaces.ts for the
// canonical list) so each component's translations can be edited in
// isolation. Vite's import.meta.glob pulls all of them in at build time -
// adding a new namespace is just adding the two JSON files, no import
// list to maintain here.
const noModules = import.meta.glob('./locales/no/*.json', { eager: true }) as Record<string, { default: Record<string, string> }>;
const enModules = import.meta.glob('./locales/en/*.json', { eager: true }) as Record<string, { default: Record<string, string> }>;

function namespaceName(path: string): string {
  return path.split('/').pop()!.replace('.json', '');
}

function buildResources(modules: Record<string, { default: Record<string, string> }>) {
  const out: Record<string, Record<string, string>> = {};
  for (const path in modules) {
    out[namespaceName(path)] = modules[path].default;
  }
  return out;
}

const resources = {
  no: buildResources(noModules),
  en: buildResources(enModules),
};

export const SUPPORTED_LANGUAGES = ['no', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Language is decided entirely by the URL ("/en/..." vs everything else -
// see index.tsx's basename and seo/pageMeta.ts's matching server-side
// logic), not by a cookie or the browser's Accept-Language header. Search
// engines don't reliably carry cookies or send a usable Accept-Language
// between crawls, so anything other than the URL being the single source
// of truth means Google only ever indexes one language for a given page.
// Toggling language (see Navbar.tsx) does a full navigation to the other
// prefix rather than an in-place i18n.changeLanguage(), which is what
// keeps this in sync with zero ambiguity.
export function detectLanguageFromPath(pathname: string): SupportedLanguage {
  return /^\/en(\/|$)/.test(pathname) ? 'en' : 'no';
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    ns: Object.keys(resources.no),
    defaultNS: 'common',
    lng: detectLanguageFromPath(window.location.pathname),
    fallbackLng: 'no',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
  });

document.documentElement.lang = i18n.language.startsWith('en') ? 'en' : 'no';

export default i18n;
